import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey } from "common/materials/materialSrc";
import { isLibraryHatchMaterial } from "geom/hatchLibrary";
import { HatchMaterialId } from "geom/hatchMaterialId";
import type { HatchMaterial } from "geom/hatchTypes";
import { hatchPatternMaterialNew } from "geom/hatchTypes";
import { hatchMaterialFromDesignMaterial } from "gfx/hatchMaterialFromDesignMaterial";
import { getCachedLibraryHatchMaterial } from "gfx/hatchMaterialLoader";
import { HTTPMaterialParameterLoader } from "gfx/HTTPMaterialParameterLoader";
import { preprocessMaterialParams } from "gfx/materials/designMaterialParams";

class HatchMaterialFactory {
  private readonly hatchMaterialCache: { [key: string]: Promise<HatchMaterial> } = {}; // Only used for library materials, keys are hatch material ids.
  private readonly designMaterialHatchCache: { [key: string]: Promise<HatchMaterial> } = {}; // Keys are design material ids.

  readonly emptyHatchPattern = hatchPatternMaterialNew([]);

  async getLibraryHatchMaterial(hatchMaterialId: HatchMaterialId): Promise<HatchMaterial> {
    return getCachedLibraryHatchMaterial(hatchMaterialId, this.hatchMaterialCache);
  }

  async getHatchMaterialForDesignMaterial(designMaterialId: MaterialSrc | undefined): Promise<HatchMaterial> {
    if (!designMaterialId) {
      return this.getLibraryHatchMaterial(HatchMaterialId.None);
    }

    const cacheKey = materialCacheKey(designMaterialId);

    if (cacheKey in this.designMaterialHatchCache) {
      return this.designMaterialHatchCache[cacheKey];
    }

    return (this.designMaterialHatchCache[cacheKey] = new Promise<HatchMaterial>((resolve) => {
      void (async () => {
        try {
          const designMaterialParams =
            await HTTPMaterialParameterLoader.instance.fetchDesignMaterialParams(designMaterialId);
          if (!designMaterialParams) {
            return;
          }
          const hatchMaterialId = designMaterialParams.hatchMaterialId;

          if (isLibraryHatchMaterial(hatchMaterialId)) {
            const hatchMaterial = await this.getLibraryHatchMaterial(hatchMaterialId as HatchMaterialId);
            resolve(hatchMaterial);
            return;
          }

          const hatchMaterial = hatchMaterialFromDesignMaterial(preprocessMaterialParams(designMaterialParams));
          resolve(hatchMaterial);
        } catch (e) {
          console.error("Could not make hatch material for design material: ", designMaterialId, e);
          resolve(this.emptyHatchPattern);
        }
      })();
    }));
  }
}

export const defaultHatchMaterialFactory = new HatchMaterialFactory();
